import { LayerIndex, SidePanelLayerType } from "@nova/constants"
import {
    selectOrthoFlightById,
    selectOrthoFlights,
} from "features/entity-caching/flights/selectors"
import { ModalNames } from "features/modals/types"
import {
    createOrthoFlight,
    deleteOrthoFlight,
    updateOrthoFlight,
} from "features/pubsub/entityActions/flights/ortho"
import { useAppSelector } from "integrations/redux/hooks"
import { selectAllOrthoBoundingBoxes } from "./OrthomosaicSlice"
import { selectIsOrthoComparisonActive } from "./OrthoComparisonSlice"
import { selectOrthoMaskEditingFlightId } from "./OrthoMaskSlice"
import { OrthoFlight } from "@nova/protobuf"
import * as Icons from "@nova/component-icons"
import { PluginRegistry } from "plugins/plugin-registry"
import { zoomToOrtho } from "features/map/inspector-panel/orthos/module"
import { getGenericLayerDataInspectorPanelComponent } from "features/map/inspector-panel/inspector-panels/GenericInspectorPanel"
import { InspectorPanelOrthoLayer } from "features/map/inspector-panel/orthos/InspectorPanelOrthoLayer"
import { InspectorPanelOrthoData } from "features/map/inspector-panel/orthos/InspectorPanelOrthoData"
import { InspectorPanelOrthoComparisonPanel } from "features/map/inspector-panel/orthos/InspectorPanelOrthoComparisonPanel"
import { OrthoMaskPanel } from "features/map/inspector-panel/orthos/OrthoMaskPanel"
import { OrthoMaskDrawingOverlay } from "./OrthoMaskDrawingOverlay"
import { getGenericUploadFlowActiveFeatureSidePanelComponent } from "panels/content/side-panel/active-feature-components/GenericActiveFeatureSidePanelComponent"
import { OrthoMultifileUpload } from "./OrthomosaicMultifileUpload"
import { useOrthoStatusAndProgress } from "panels/content/side-panel/progress/Ortho"
import { getGenericAssetsListItem } from "panels/content/side-panel/generics/genericAssetsListItems"
import { getGenericLayerListItem } from "panels/content/side-panel/generics/GenericLayerListItem"
import { getBadgeComponent } from "panels/content/side-panel/generics/GenericLayerListItemProgress"
import { GenericFlightAssetsThumbnail } from "panels/content/side-panel/generics/genericAssetsListThumbnails"
import { useRGBOrthoOverlapLayer } from "./layers/overlap"
import { useRGBOrthoLayer } from "./layers/ortho"
import { useRGBOrthoScanAreaLayer } from "./layers/scanArea"
import { useOrthoComparisonDivider } from "./layers/useOrthoComparisonDivider"
import { useOrthoComparisonBoostLayer } from "./layers/useOrthoComparisonBoostLayer"
import { useOrthoMaskLayer } from "./layers/useOrthoMaskLayer"
import { InspectorPanelPlugin } from "features/map/inspector-panel/useInspectorPanel"

export function registerOrthomosaicPlugin(registry: PluginRegistry) {
    registry
        .register({
            name: "ortho",
            displayName: "Orthomosaic",
            sidePanelLayerType: SidePanelLayerType.OrthomosaicFlight,
            PrimaryIcon: Icons.HotspotOrthoIcon,
            ImageIconUrl: "/images/plugins/layers/ortho.svg",
            updateAction: updateOrthoFlight,
            deleteAction: deleteOrthoFlight,
            confirmDelete: () => true,
            protobufMessage: OrthoFlight,
            selectAllEntities: selectOrthoFlights,
            selectSingleEntity: selectOrthoFlightById,
            useUploadStatusAndProgress: useOrthoStatusAndProgress,
            zoomTo: zoomToOrtho,
            duplicate: () => undefined,
            search: (input, entity) =>
                entity.name.toLowerCase().includes(input.toLowerCase()) ? entity.name : undefined,
        })
        .implement("side-panel-layers-plugin", (spec) => ({
            SidePanelListItem: getGenericLayerListItem({
                spec,
                RightSlotComponent: getBadgeComponent({
                    useStatusAndProgress: useOrthoStatusAndProgress,
                }),
            }),
        }))
        .implement("side-panel-assets-plugin", (spec) => ({
            SidePanelListItem: getGenericAssetsListItem({
                spec,
                ThumbnailRender: GenericFlightAssetsThumbnail,
            }),
        }))
        .implement("side-panel-active-feature-plugin", () => ({
            SidePanelComponent: getGenericUploadFlowActiveFeatureSidePanelComponent({
                MultifileUploadComponent: OrthoMultifileUpload,
            }),
        }))
        .implement("inspector-panel-plugin", (spec) => {
            const NormalOrthoPanel = getGenericLayerDataInspectorPanelComponent({
                spec,
                LayerComponent: InspectorPanelOrthoLayer,
                DataComponent: InspectorPanelOrthoData,
                isScrollable: true,
            })
            return {
                InspectorPanelComponent: ({
                    id,
                    plugin,
                }: {
                    id?: string
                    plugin: InspectorPanelPlugin
                }) => {
                    const comparisonIsActive = useAppSelector(
                        selectIsOrthoComparisonActive(id ?? "")
                    )
                    const editingFlightId = useAppSelector(selectOrthoMaskEditingFlightId)
                    if (!id) return null
                    if (comparisonIsActive) return <InspectorPanelOrthoComparisonPanel />
                    if (editingFlightId === id) {
                        return <OrthoMaskPanel flightId={id} />
                    }
                    return <NormalOrthoPanel id={id} plugin={plugin} />
                },
            }
        })
        .implement("bounding-box-data-provider", () => ({
            useBoundingBoxData: () => useAppSelector(selectAllOrthoBoundingBoxes),
            showLabel: true,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useRGBOrthoOverlapLayer,
            zIndex: LayerIndex.OrthoOverlap,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useRGBOrthoLayer,
            zIndex: LayerIndex.Ortho,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useRGBOrthoScanAreaLayer,
            zIndex: LayerIndex.OrthoOverlap,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useOrthoComparisonBoostLayer,
            zIndex: LayerIndex.OrthoComparisonBoost,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useOrthoComparisonDivider,
            zIndex: LayerIndex.OrthoComparison,
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: useOrthoMaskLayer,
            zIndex: LayerIndex.FirePerimeterPreview,
        }))
        .implement("map-modal-plugin", () => ({
            Modal: OrthoMaskDrawingOverlay,
        }))
        .implement("feature-plugin", () => ({
            createAction: createOrthoFlight,
            sampleDataModalName: ModalNames.TryOrthomosaicsModal,
        }))
}
